import React, { type PropsWithChildren, useRef } from 'react';
import { BlurView } from 'expo-blur';
import { StyleSheet, type StyleProp, type ViewInstance, type ViewStyle } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  interpolateColor,
  type WithTimingConfig,
  useAnimatedStyle,
  useDerivedValue,
  withTiming,
} from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarGlassView = (props: Props) => {
  const { children, canHover = false, rounded = 'full', shadow = true, style } = props;

  // ─── Refs ──────────────────────────────────────────────────────────────────

  const containerRef = useRef<ViewInstance>(null);
  const isHovered = useHovered(containerRef);

  // ─── Animated Values ───────────────────────────────────────────────────────

  const hoverSV = useDerivedValue(() => {
    if (canHover && isHovered) {
      return withTiming(1, GLASS_TIMING_CONFIG);
    }

    return withTiming(0, GLASS_TIMING_CONFIG);
  });

  // ─── Animated Styles ───────────────────────────────────────────────────────

  const backgroundContainerAnimatedStyle = useAnimatedStyle(() => {
    return {
      backgroundColor: interpolateColor(
        hoverSV.value,
        GLASS_HOVER_INPUT_RANGE,
        GLASS_BACKGROUND_COLORS,
      ),
    };
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const roundedStyle = roundedStyles[rounded];
  const containerStyles = [roundedStyle, style];
  const shadowContainerStyles = [styles.shadowContainer, roundedStyle];
  const blurContainerStyles = [styles.blurContainer, roundedStyle];
  const backgroundContainerStyles = [
    styles.backgroundContainer,
    roundedStyle,
    backgroundContainerAnimatedStyle,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <Animated.View ref={containerRef} style={containerStyles}>
      {shadow ? (
        <Animated.View
          entering={GLASS_SHADOW_ENTERING}
          exiting={GLASS_SHADOW_EXITING}
          pointerEvents="none"
          style={shadowContainerStyles}
        />
      ) : undefined}

      <BlurView
        intensity={GLASS_INTENSITY}
        pointerEvents="none"
        style={blurContainerStyles}
        tint="light"
      />

      <Animated.View pointerEvents="none" style={backgroundContainerStyles} />

      {children}
    </Animated.View>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GLASS_INTENSITY = 80;
const GLASS_HOVER_INPUT_RANGE = [0, 1];
const GLASS_BACKGROUND_COLORS = ['rgba(255, 255, 255, 0.7)', 'rgba(255, 255, 255, 0.9)'];
const GLASS_TIMING_CONFIG: WithTimingConfig = { duration: theme.transitions.base };
const GLASS_SHADOW_ENTERING = FadeIn.duration(theme.transitions.short);
const GLASS_SHADOW_EXITING = FadeOut.duration(theme.transitions.short);

const NAVBAR_GLASS_BOX_SHADOW = '0px 8px 40px 4px rgba(14, 21, 14, 0.09)';

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  shadowContainer: {
    ...StyleSheet.absoluteFill,
    boxShadow: NAVBAR_GLASS_BOX_SHADOW,
  },
  blurContainer: {
    ...StyleSheet.absoluteFill,
  },
  backgroundContainer: {
    ...StyleSheet.absoluteFill,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
  },
});

const roundedStyles = StyleSheet.create<Record<Rounded, ViewStyle>>({
  large: { borderRadius: theme.radius.xlarge },
  full: { borderRadius: 1000 },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  canHover?: boolean;
  rounded?: Rounded;
  shadow?: boolean;
  style?: StyleProp<ViewStyle>;
}>;

type Rounded = 'full' | 'large';
